/**
 * Feature flags configuration for the LLM as a Service Admin Portal
 * 
 * This module provides centralized feature flag management for enabling/disabling
 * features across the application. All feature flags should be defined here
 * with clear naming and documentation.
 */

export interface FeatureFlags {
  // Integration page features
  enableZapierIntegration: boolean;
  
  // Future feature flags can be added here
  // enableBetaFeatures: boolean;
  // enableAdvancedAnalytics: boolean;
  // enableExperimentalUI: boolean;
}

/**
 * Default feature flag configuration
 * These values control which features are enabled by default
 */
const defaultFeatureFlags: FeatureFlags = {
  // Zapier integration is disabled by default
  enableZapierIntegration: false,
};

/**
 * Environment-based feature flag overrides
 * This allows different configurations for different environments
 */
const environmentOverrides: Partial<FeatureFlags> = {
  // Example: Enable certain features in development
  // ...(process.env.NODE_ENV === 'development' && { enableBetaFeatures: true }),
  
  // Example: Enable features based on environment variables
  // ...(process.env.REACT_APP_ENABLE_ZAPIER === 'true' && { enableZapierIntegration: true }),
};

/**
 * Final feature flags configuration
 * Combines default flags with environment overrides
 */
export const featureFlags: FeatureFlags = {
  ...defaultFeatureFlags,
  ...environmentOverrides,
};

/**
 * Helper function to check if a feature is enabled
 * @param featureName - The name of the feature flag to check
 * @returns boolean indicating if the feature is enabled
 */
export const isFeatureEnabled = (featureName: keyof FeatureFlags): boolean => {
  return featureFlags[featureName];
};

/**
 * Helper hook for React components to check feature flags
 * @param featureName - The name of the feature flag to check
 * @returns boolean indicating if the feature is enabled
 */
export const useFeatureFlag = (featureName: keyof FeatureFlags): boolean => {
  return isFeatureEnabled(featureName);
};
